import { useCallback, useRef } from 'react';

import { useModal } from 'mui-modal-provider';
import { matchPath, useLocation } from 'react-router-dom';

import {
  guessMainProvider,
  useClusters,
  isKentEligibleCluster,
} from '@cast/shared-cluster-context-data-access';
import {
  useExhaustedTrialDialog,
  useInitializeTrialDialog,
  useReportingTrial,
  useTrial,
} from '@cast/shared-trial-ui';
import { K8sProvider } from '@cast/shared-types';
import { Cluster } from '@cast/types';
import { DEMO_CLUSTER_ID } from '@cast/utils';

import { useIsUserTriggeredDemoMode } from '@cast/infrastructure-demo';
import {
  PRODUCT_BASE_PATHS,
  premiumFeaturePaths,
} from '@cast/console-constants';
import { useIsSecuritySunsetEnabled } from 'features/organization/security-insights/hooks/useIsSecuritySunsetEnabled';
import { useDefaultPhase2SuccessNavigation } from 'hooks/dialogs/cluster/_hooks/useDefaultPhase2SuccessNavigation';
import { FeatureName, FeatureSelection } from 'hooks/dialogs/cluster/types';
import { useNavigate } from '@cast/infrastructure-organization';

import { ClusterOnboardingDialog } from './ClusterOnboardingDialog';

import { ShowModalOutput } from '@cast/design-system';

type OnboardClusterDialogProps = {
  initialProvider?: K8sProvider;
  initialAnywhereClusterName?: string;
};

type Result = {
  openOnboardClusterDialog(promotedFeature?: FeatureName): void;
  isLoading: boolean;
};

export const useOnboardClusterDialog = ({
  initialProvider,
  initialAnywhereClusterName,
}: OnboardClusterDialogProps = {}): Result => {
  const { clusters, isLoading } = useClusters({ polling: false });
  const navigate = useNavigate();
  const modalInstance = useRef<ShowModalOutput>(undefined);
  const { showModal } = useModal();

  const _initialProvider = initialProvider || guessMainProvider(clusters);

  const location = useLocation();
  const isSecuritySunsetEnabled = useIsSecuritySunsetEnabled();
  // TODO: Jesus Crist, please stop relaying on strings in the future
  const isSecurityFlow =
    !isSecuritySunsetEnabled &&
    (location.pathname.includes('organization/security') ||
      location.pathname.startsWith('/security'));

  const onSuccessNavigation = useDefaultPhase2SuccessNavigation();

  const { isEligible, state } = useTrial();
  const {
    isPreTrial: isReportingPreTrial,
    isEligible: isReportingEligible,
    isLoading: isReportingLoading,
    startTrial: startReportingTrial,
    isStartingTrial,
  } = useReportingTrial();
  const isDemoMode = useIsUserTriggeredDemoMode();

  const wasClusterConnectedRef = useRef(false);
  // PLG cluster-count nudge — skip when reporting monetization owns this org.
  const shouldNudgeRef = useRef(false);
  shouldNudgeRef.current =
    !isReportingLoading &&
    !isReportingEligible &&
    isEligible &&
    state === 'pre-trial' &&
    [3, 5].includes(clusters.length);

  const isReportingPreTrialRef = useRef(isReportingPreTrial);
  isReportingPreTrialRef.current = isReportingPreTrial;
  const isStartingTrialRef = useRef(isStartingTrial);
  isStartingTrialRef.current = isStartingTrial;
  const reportingTrialStartRequestedRef = useRef(false);

  const selectedFeaturesRef = useRef<FeatureSelection | null>(null);
  const connectedClusterRef = useRef<Cluster | null>(null);

  const onClusterConnected = useCallback(
    (cluster: Cluster) => {
      wasClusterConnectedRef.current = true;
      connectedClusterRef.current = cluster;

      // Reporting monetization: connection starts the trial (Loom / Figma).
      // Read status via refs so late status updates after dialog open still apply.
      // One-shot per dialog session — callback can fire more than once.
      if (
        isReportingPreTrialRef.current &&
        !isStartingTrialRef.current &&
        !reportingTrialStartRequestedRef.current
      ) {
        reportingTrialStartRequestedRef.current = true;
        startReportingTrial();
      }

      if (location.pathname.includes(DEMO_CLUSTER_ID)) {
        const isPremiumPath = premiumFeaturePaths.some((path) =>
          matchPath(path, location.pathname)
        );
        if (isPremiumPath) {
          navigate('/dashboard');
        } else {
          navigate(location.pathname.replace(DEMO_CLUSTER_ID, cluster.id));
        }
      }
    },
    [location.pathname, navigate, startReportingTrial]
  );

  const onFeaturesSelected = useCallback((features: FeatureSelection) => {
    selectedFeaturesRef.current = features;
  }, []);

  const closeModal = useCallback(() => {
    modalInstance.current?.destroy();
  }, []);

  const { openDialog: openInitializeTrialDialog } = useInitializeTrialDialog();
  const { openDialog: openExhaustedTrialDialog } = useExhaustedTrialDialog();

  const nudgeTrialIfRelevant = useCallback(() => {
    // nudge if the cluster has been connected during the onboarding flow
    if (wasClusterConnectedRef.current && shouldNudgeRef.current) {
      openInitializeTrialDialog();
    }
  }, [openInitializeTrialDialog]);

  const handleCompleted = useCallback(() => {
    closeModal();
    nudgeTrialIfRelevant();

    const cluster = connectedClusterRef.current;
    const selectedFeatures = selectedFeaturesRef.current;

    if (cluster && selectedFeatures) {
      if (selectedFeatures.kent) {
        if (!isKentEligibleCluster(cluster)) {
          navigate(
            `${PRODUCT_BASE_PATHS.automation}/external-clusters/${cluster.id}/dashboard`
          );
        }
      } else {
        onSuccessNavigation(cluster, selectedFeatures);
      }
    }
  }, [navigate, closeModal, nudgeTrialIfRelevant, onSuccessNavigation]);

  const handleClose = useCallback(() => {
    closeModal();
    nudgeTrialIfRelevant();

    // was closed at any step after selecting features, but before completion
    if (isSecurityFlow && selectedFeaturesRef.current) {
      // Redirect to the Compliance page when the cluster is still in phase 1
      // since it is the only page that handles phase 1 clusters
      navigate({
        pathname: '/organization/security/compliance',
      });
    }
  }, [isSecurityFlow, navigate, closeModal, nudgeTrialIfRelevant]);

  const openOnboardClusterDialog = (promotedFeature?: FeatureName) => {
    reportingTrialStartRequestedRef.current = false;
    modalInstance.current = showModal(
      ClusterOnboardingDialog,
      {
        onClose: handleClose,
        onCompleted: handleCompleted,
        onFeaturesSelected,
        onClusterConnected,
        initialProvider: _initialProvider,
        initialAnywhereClusterName,
        promotedFeature:
          promotedFeature ?? (isSecurityFlow ? 'security' : undefined),
      },
      { rootId: 'root' }
    );
  };

  // PLG "Try Cast AI for free" gate before connect — not for reporting-eligible orgs.
  // Wait for reporting status so temporary isEligible:false while loading is not treated as ineligibility.
  if (
    !isReportingLoading &&
    !isReportingEligible &&
    isEligible &&
    state === 'pre-trial'
  ) {
    if (isDemoMode) {
      return {
        openOnboardClusterDialog: (promotedFeature?: FeatureName) =>
          openInitializeTrialDialog(undefined, {
            onClose: (trialInitialized) => {
              if (trialInitialized === true) {
                openOnboardClusterDialog(promotedFeature);
              }
            },
          }),
        isLoading,
      };
    }

    if (clusters.length >= 5) {
      return {
        openOnboardClusterDialog: (promotedFeature?: FeatureName) =>
          openExhaustedTrialDialog(undefined, {
            onClose: (trialInitialized) => {
              if (trialInitialized === true) {
                openOnboardClusterDialog(promotedFeature);
              }
            },
          }),
        isLoading,
      };
    }
  }

  return {
    openOnboardClusterDialog,
    isLoading,
  };
};
